import React, { useState, useEffect, useRef } from 'react';
import { Ico } from './icons';
import { COLORS, COLOR_HEX } from './icons';

// Page list (left) — Figma-style: doc info at top, pages below
export function PageList({ doc, currentPageId, onSelect, collapsed, onToggle }) {
  const listRef = useRef(null);
  const allPages = doc.tabs.filter(t => t.inline || t.label).flatMap(t =>
    t.pageIds.filter(pid => doc.pages[pid]).map(pid => ({ pid, tab: t, page: doc.pages[pid] }))
  );

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('.pl-page-item--selected');
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentPageId]);

  const panelBtn = (
    <button type="button" className="tt-btn" onClick={onToggle} data-tip={collapsed ? 'Maximize UI' : 'Minimize UI'} aria-label={collapsed ? 'Maximize UI' : 'Minimize UI'}>
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M5.5 2.5v11" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
    </button>
  );

  if (collapsed) {
    return (
      <div className="pagelist collapsed">
        {panelBtn}
      </div>
    );
  }

  return (
    <div className="pagelist">
      <div className="pl-header">
        <span className="pl-header__title">Pages</span>
        {panelBtn}
      </div>
      <div className="pl-page-list" ref={listRef}>
        {allPages.map(({ pid, tab, page }, idx) => (
          <div key={pid} className={`pl-page-item${currentPageId===pid?' pl-page-item--selected':''}`} onClick={()=>onSelect(pid)}>
            <div className={`pl-page-item__thumb thumb-${tab?.location||'left'}`}>
              <div className="pl-page-item__tab-mark"
                   style={tab?.color
                     ? {background: COLOR_HEX[tab.color]}
                     : {background:'var(--color-bg-surface-selected)', boxShadow:'inset 0 0 0 1px var(--color-border-secondary)'}}/>
              <div className="pl-page-item__line pl-page-item__line--h"/>
              <div className="pl-page-item__line"/>
              <div className="pl-page-item__line pl-page-item__line--s"/>
              <div className="pl-page-item__line"/>
              <div className="pl-page-item__line pl-page-item__line--xs"/>
            </div>
            <div className="pl-page-item__meta">
              <span className="pl-page-item__number">{idx + 1}</span>
              <span className="pl-page-item__title">{tab.label||'Untitled'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SizePicker({ doc, orientation, onSetRailSize }) {
  const SIZES = [['small','S'],['medium','M'],['large','L']];
  const cur = (loc) => (doc.railSizes && doc.railSizes[loc]) || 'small';
  const sideButtons = (loc) => SIZES.map(([k,l])=>(
    <button key={k} className={`sp-btn seg-control__btn${cur(loc)===k?' seg-control__btn--active':''}`}
            onClick={()=>onSetRailSize(loc, k)} title={`${loc} · ${k}`}>{l}</button>
  ));
  return (
    <div className={`size-picker sp-${orientation==='landscape'?'landscape':'portrait'}`}>
      <div className="sp-side sp-top">{sideButtons('top')}</div>
      <div className="sp-side sp-left">{sideButtons('left')}</div>
      <div className="sp-center">Page</div>
      <div className="sp-side sp-right">{sideButtons('right')}</div>
      <div className="sp-side sp-bottom">{sideButtons('bottom')}</div>
    </div>
  );
}

// Page structure settings (right)
export function StructureSettings({ doc, page, onUpdateDoc, onUpdatePage, onSetRailSize }) {
  return (
    <div className="settings">

      <div className="sp-scroll-area">
      <div className="cds--side-panel__body">
        <div className="cds--form-item">
          <label className="cds--label">Orientation</label>
          <div className="seg-control seg-control--sm seg-control--full">
            <button className={`seg-control__btn${(doc.orientation||'portrait')==='portrait'?' seg-control__btn--active':''}`}
                    onClick={()=>onUpdateDoc({orientation:'portrait'})}>
              <span className="seg-control__label"><Ico.Portrait width="14" height="14"/> Portrait</span>
            </button>
            <button className={`seg-control__btn${doc.orientation==='landscape'?' seg-control__btn--active':''}`}
                    onClick={()=>onUpdateDoc({orientation:'landscape'})}>
              <span className="seg-control__label"><Ico.Landscape width="14" height="14"/> Landscape</span>
            </button>
          </div>
        </div>

        <div className="cds--form-item">
          <label className="cds--label">Tab size per side</label>
          <SizePicker doc={doc} orientation={doc.orientation||'portrait'} onSetRailSize={onSetRailSize}/>
          <div className="cds--form__helper-text">Shared across all pages. Larger tabs are easier to tap in flight.</div>
        </div>
      </div>
      </div>

    </div>
  );
}
